import type { RenderBackend } from "./render/types";
import { KeyboardInput } from "./core/input";
import { createPlayerState, updatePlayerLogic, DEFAULT_PLAYER_CONFIG } from "./core/player_logic";
import { animMap, PLACEMENTS, TURTLE_PERSONAS } from "./core/asset_manifest";

async function main() {
  const canvas = document.getElementById("renderCanvas") as HTMLCanvasElement;
  if (!canvas) throw new Error("renderCanvas missing");

  const backendName = new URLSearchParams(location.search).get("backend") ?? "babylon";
  const backend = await createBackend(backendName);
  await backend.init(canvas);
  await backend.loadWorld();
  await backend.createPlayer({ x: 0, y: 4, z: 0 });

  const input = new KeyboardInput();
  const animNames = Object.keys((animMap as { matches: Record<string, number> }).matches);
  const playerState = createPlayerState(0, animNames);

  backend.createCamera(canvas, {
    getYaw: () => playerState.yaw,
    addYaw: (d) => { playerState.yaw += d; },
  });

  const propResults = await backend.loadProps(PLACEMENTS);
  console.info(`[shellhaven] loaded ${propResults.loaded}/${propResults.total} props (${propResults.missing.length} missing)`);
  if (propResults.missing.length > 0) {
    console.warn("[shellhaven] missing models:", propResults.missing);
  }

  backend.snapPropsToGround();
  backend.attachTurtles({ roamRadius: 3, walkSpeed: 0.35, idleSeconds: [2, 5] });
  backend.setupNameplates(TURTLE_PERSONAS);
  await backend.equipWeapon("wooden_sword");
  backend.applyPostFX();

  backend.runRenderLoop(() => {
    const dt = backend.getDeltaTime();
    const physics = backend.getPlayerPhysics();
    const cmd = updatePlayerLogic(dt, input, physics, playerState, backend.getCameraAlpha(), DEFAULT_PLAYER_CONFIG);
    backend.resolveEmote(playerState, cmd);
    backend.applyPlayerCommand(cmd, playerState);
    backend.syncCamera();
    backend.render();
  });

  window.addEventListener("resize", () => backend.resize());

  // @ts-expect-error - dev-only handle on window
  window.__shellhaven = backend.getDevHandles();
}

async function createBackend(name: string): Promise<RenderBackend> {
  if (name === "three") {
    const { ThreeBackend } = await import("./render/three/ThreeBackend");
    return new ThreeBackend();
  }
  const { BabylonBackend } = await import("./render/babylon/BabylonBackend");
  return new BabylonBackend();
}

main().catch((err) => {
  console.error("[shellhaven] fatal:", err);
  const loader = document.getElementById("loader");
  if (loader) {
    loader.innerHTML = `<div style="color:#a44">⚠ failed to start: ${String(err?.message ?? err)}</div>`;
  }
});
